import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import ClerkStatusBanner from '@/components/ClerkStatusBanner';
import EodCheckBanner from '@/components/EodCheckBanner';
import ClerkLoginReminder from '@/components/layout/ClerkLoginReminder';
import InactivityLock from '@/components/InactivityLock';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  LayoutDashboard, 
  FileText, 
  User, 
  Users, 
  Upload, 
  ClipboardList,
  Download,
  LogOut,
  Menu,
  X,
  Truck,
  FileSignature,
  History,
  Briefcase,
  KeyRound,
  UserCog,
  Archive,
  Package,
  ListTodo,
  Trophy,
  ChevronDown,
  ChevronRight,
  FilePlus,
  FileStack,
  DollarSign,
  PiggyBank,
  Receipt,
  Landmark,
  ArrowLeftRight,
  ArrowUpRight,
  Tags,
  HelpCircle,
  Settings,
  Shield,
  EyeOff,
  CalendarDays,
  Calendar,
  TrendingUp,
  BookOpen,
  FolderCog,
  Building2,
  Brain,
  ShoppingCart,
  Fuel,
  Wrench,
  BarChart3,
  Home,
  MapPin,
  Scale,
  ShieldCheck,
  ScanSearch,
  Target,
  HandCoins,
  CalendarClock,
  Sparkles,
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    statements: false,
    finance: false,
    banking: false,
    // Priority shortcuts section — start expanded so managers see it immediately.
    'sec-quickview': true,
  });
  // Admin-controlled hidden sidebar paths (fetched once per mount)
  const [hiddenPaths, setHiddenPaths] = useState(new Set());

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const ctrl = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-visibility`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
    })
      .then(r => r.ok ? r.json() : { hidden: [] })
      .then(d => setHiddenPaths(new Set(d.hidden || [])))
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  const handleLogout = async () => {
    // Hard-block sign-out after 16:40 SAST if the End-of-Day Administration
    // Check has not been completed today. Silent for anyone who isn't a
    // required role or is signing out before the workday cut-off.
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await fetch(`${API_URL}/api/eod-check/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const s = await res.json();
          // The cut-off — after 16:40 SAST the ack becomes truly mandatory.
          const sast = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
          const pastCutoff = sast.getHours() > 16 || (sast.getHours() === 16 && sast.getMinutes() >= 40);
          if (s.required && pastCutoff && !s.acknowledged) {
            toast.error(
              'End-of-Day check not acknowledged — please tick the red banner before signing out.',
              { duration: 6000 }
            );
            // Scroll to top so the banner is unmissable
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        }
      } catch { /* fall through and allow logout if status check fails */ }
    }
    logout();
    navigate('/login');
  };

  // "Home" target depends on role — admin/clerk go to /admin, salesman to
  // /auto-spares, driver to /driver, etc.
  const homePath = (() => {
    const role = user?.role;
    if (role === 'driver') return '/driver';
    if (role === 'internal_staff') return '/staff';
    if (role === 'auto_spares_salesman') return '/auto-spares';
    if (role === 'hr_admin') return '/hr';
    return '/admin';
  })();
  const handleHome = () => navigate(homePath);

  const toggleMenu = (menu) => {
    setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const driverLinks = [
    { path: '/driver', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/driver/shift', icon: MapPin, label: 'My Shift' },
    { path: '/driver/statements', icon: FileText, label: 'Statements' },
    { path: '/driver/contracts', icon: FileSignature, label: 'Contracts' },
    { path: '/driver/profile', icon: User, label: 'Profile' },
  ];

  // Admin menu — grouped into collapsible top-level sections.
  // `type: 'section'` is a top-level collapsible header that can hold either
  // plain links or existing `type: 'group'` sub-groups (used for Finance /
  // Banking / Statements which each already have their own nested menu).
  const adminMenu = [
    // Dashboard link always visible at the very top so admins can jump back
    // to the main /admin dashboard in one click.
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },

    {
      type: 'section', label: 'Dashboard', icon: LayoutDashboard, key: 'sec-dashboard',
      items: [
        { path: '/admin/group-overview', icon: Building2, label: 'JSM Group Overview' },
        { path: '/admin/financial-advisor', icon: Sparkles, label: 'AI Financial Advisor' },
      ],
    },
    {
      type: 'section', label: 'Management Quick View', icon: Trophy, key: 'sec-quickview',
      items: [
        { path: '/admin/fuel-tracker', icon: Fuel, label: 'Fuel Tracker' },
        { path: '/admin/fleet-maintenance', icon: Wrench, label: 'Fleet Maintenance' },
        { path: '/admin/sales-analytics', icon: BarChart3, label: 'Sales Analytics' },
        { path: '/admin/leave', icon: CalendarDays, label: 'Staff Leave Manager' },
        { path: '/admin/fleet-trips', icon: Package, label: 'Fleet Trips (Collective)' },
        { path: '/admin/tasks', icon: ListTodo, label: 'Clerk Tasks & KPIs' },
        { path: '/admin/job-cards', icon: ClipboardList, label: 'Job Cards' },
      ],
    },
    {
      type: 'section', label: 'Operations', icon: Truck, key: 'sec-operations',
      items: [
        { path: '/admin/drivers', icon: Users, label: 'Owner Drivers' },
        { path: '/admin/driver-shifts', icon: MapPin, label: 'Driver Shifts (Live)' },
        { path: '/admin/daily-attendance', icon: CalendarClock, label: 'Daily Attendance' },
        { path: '/admin/vehicle-change-requests', icon: Truck, label: 'Vehicle Change Requests' },
        { path: '/admin/fleet-trips', icon: Package, label: 'Fleet Trips (Collective)' },
        { path: '/admin/clerks', icon: UserCog, label: 'Transport Clerks' },
        { path: '/admin/tasks', icon: ListTodo, label: 'Clerk Tasks & KPIs' },
        { path: '/admin/job-cards', icon: ClipboardList, label: 'Job Cards' },
        { path: '/admin/tenders', icon: Briefcase, label: 'Live Tenders' },
        { path: '/admin/sites', icon: MapPin, label: 'Sites' },
        { path: '/admin/performance', icon: Trophy, label: 'Performance & Incentives' },
        { path: '/admin/incentive-settings', icon: Target, label: 'Parcel Targets & Bonus' },
      ],
    },
    {
      type: 'section', label: 'HR & Staff', icon: UserCog, key: 'sec-hr',
      items: [
        { path: '/admin/employees', icon: UserCog, label: 'Employees' },
        { path: '/admin/company-register', icon: CalendarDays, label: 'Company Register (All Staff)' },
        { path: '/admin/staff-register', icon: Calendar, label: 'Staff Attendance Register' },
        { path: '/admin/leave', icon: CalendarDays, label: 'Staff Leave Manager' },
        { path: '/admin/internal-staff', icon: Truck, label: 'Internal Staff & Payslips' },
        { path: '/admin/driver-revenue', icon: TrendingUp, label: 'Driver Revenue' },
        { path: '/admin/all-payslips', icon: Receipt, label: 'All Payslips (Group)' },
        { path: '/admin/staff-loans', icon: HandCoins, label: 'Staff Loan Requests' },
        { path: '/hr', icon: Scale, label: 'Virtual Mediator' },
      ],
    },
    {
      type: 'section', label: 'Fleet', icon: Fuel, key: 'sec-fleet',
      items: [
        { path: '/admin/fuel-tracker', icon: Fuel, label: 'Fuel Tracker' },
        { path: '/admin/fleet-maintenance', icon: Wrench, label: 'Fleet Maintenance' },
      ],
    },
    {
      type: 'section', label: 'Auto Spares', icon: ShoppingCart, key: 'sec-autospares',
      items: [
        { path: '/auto-spares/admin', icon: ShoppingCart, label: 'Auto Spares Sales' },
        { path: '/admin/sales-analytics', icon: BarChart3, label: 'Sales Analytics' },
        { path: '/admin/pricing-monitor', icon: ShieldCheck, label: 'Pricing Monitor' },
      ],
    },
    {
      type: 'section', label: '2K2DAY', icon: Brain, key: 'sec-2k2day',
      items: [
        { path: '/loans-tracker', icon: FileText, label: '2K2Day Loan Tracker' },
        { path: '/vetting', icon: ScanSearch, label: '2K2Day Vetting Engine' },
      ],
    },
    {
      type: 'section', label: 'Finance', icon: DollarSign, key: 'sec-finance',
      items: [
        {
          type: 'group', label: 'Finance', icon: DollarSign, key: 'finance',
          items: [
            { path: '/admin/finance/year', icon: Calendar, label: 'Financial Year' },
            { path: '/admin/finance/dashboard', icon: PiggyBank, label: 'Income vs Expenses' },
            { path: '/admin/finance/income', icon: ArrowUpRight, label: 'Income' },
            { path: '/admin/finance/expenses', icon: Receipt, label: 'Expenses' },
            { path: '/admin/finance/vat', icon: Building2, label: 'VAT Summary' },
            { path: '/admin/finance/forecast', icon: TrendingUp, label: 'Forecast' },
            { path: '/admin/finance/cashflow', icon: ArrowLeftRight, label: 'Cash Flow' },
            { path: '/admin/finance/owner-driver-payouts', icon: Truck, label: 'Owner Driver Payouts' },
            { path: '/admin/finance/transport-revenue', icon: TrendingUp, label: 'JSM Transport Revenue' },
            { path: '/admin/finance/transport-expenses', icon: Receipt, label: 'JSM Transport Expenses' },
            { path: '/admin/finance/transport-pnl', icon: BarChart3, label: 'JSM Transport P&L' },
            { path: '/admin/finance/driver-pnl', icon: TrendingUp, label: 'Per-Driver P&L' },
            { path: '/admin/finance/categories', icon: FolderCog, label: 'Categories' },
            { path: '/admin/finance/gl-codes', icon: BookOpen, label: 'GL Codes' },
          ],
        },
        {
          type: 'group', label: 'Banking', icon: Landmark, key: 'banking',
          items: [
            { path: '/admin/banking/import', icon: Upload, label: 'Import Statement' },
            { path: '/admin/banking/transactions', icon: ArrowLeftRight, label: 'Transactions Ledger' },
            { path: '/admin/banking/rules', icon: Tags, label: 'Categories & Rules' },
            { path: '/admin/banking/uncategorized', icon: HelpCircle, label: 'Uncategorized Queue' },
          ],
        },
        {
          type: 'group', label: 'Statements', icon: FileText, key: 'statements',
          items: [
            { path: '/admin/statements/generate', icon: FilePlus, label: 'Generate Statements' },
            { path: '/admin/statements', icon: Upload, label: 'Upload Statements' },
            { path: '/admin/statements/history', icon: FileStack, label: 'Statement History' },
            { path: '/admin/deductions-ledger', icon: Receipt, label: 'Deductions Ledger' },
          ],
        },
        { path: '/admin/exports', icon: Download, label: 'Exports' },
      ],
    },
    {
      type: 'section', label: 'Recruitment & Registrations', icon: ClipboardList, key: 'sec-recruitment',
      items: [
        { path: '/admin/registrations', icon: ClipboardList, label: 'Registrations' },
        { path: '/admin/applications', icon: ClipboardList, label: 'New Applications' },
      ],
    },
    {
      type: 'section', label: 'Administration', icon: Settings, key: 'sec-administration',
      items: [
        { path: '/admin/credentials', icon: KeyRound, label: 'Credentials' },
        { path: '/admin/permissions', icon: Shield, label: 'Admin Permissions' },
        { path: '/admin/users', icon: KeyRound, label: 'User Manager & Passwords' },
        { path: '/admin/menu-settings', icon: EyeOff, label: 'Menu Visibility' },
        { path: '/admin/audit-logs', icon: History, label: 'Audit Logs' },
        { path: '/admin/backups', icon: Archive, label: 'Backups & Recovery' },
      ],
    },
  ];

  // Clerk menu — filtered by per-clerk permission flags returned from /auth/me.
  // Mirrors the admin menu structure so clerks see a familiar sidebar.
  const clerkPerms = user?.permissions || {};
  const financeItems = [
    clerkPerms.can_view_transport_pnl && { path: '/admin/finance/transport-pnl', icon: BarChart3, label: 'JSM Transport P&L' },
    clerkPerms.can_manage_transport_revenue && { path: '/admin/finance/transport-revenue', icon: TrendingUp, label: 'JSM Transport Revenue' },
    clerkPerms.can_manage_transport_expenses && { path: '/admin/finance/transport-expenses', icon: Receipt, label: 'JSM Transport Expenses' },
  ].filter(Boolean);
  const bankingItems = clerkPerms.can_manage_banking ? [
    { path: '/admin/banking/import', icon: Upload, label: 'Import Statement' },
    { path: '/admin/banking/transactions', icon: ArrowLeftRight, label: 'Transactions Ledger' },
    { path: '/admin/banking/rules', icon: Tags, label: 'Categories & Rules' },
    { path: '/admin/banking/uncategorized', icon: HelpCircle, label: 'Uncategorized Queue' },
  ] : [];
  const statementsItems = clerkPerms.can_view_statements ? [
    { path: '/admin/statements/generate', icon: FilePlus, label: 'Generate Statements' },
    { path: '/admin/statements', icon: Upload, label: 'Upload Statements' },
    { path: '/admin/statements/history', icon: FileStack, label: 'Statement History' },
    { path: '/admin/deductions-ledger', icon: Receipt, label: 'Deductions Ledger' },
  ] : [];

  // Clerk menu — same sectioned structure as admin, but every item is gated
  // by the clerk's per-permission flags returned from /auth/me. Sections are
  // dropped entirely when they contain no permitted items (via filterHidden).
  const clerkAutoSpares = [
    clerkPerms.can_view_auto_spares && { path: '/auto-spares/parts-catalog', icon: Package, label: 'Parts Price Catalog' },
    clerkPerms.can_view_auto_spares && { path: '/auto-spares/stock-take', icon: ClipboardList, label: 'Stock Take' },
    clerkPerms.can_view_auto_spares && { path: '/procurement', icon: ShoppingCart, label: 'Procurement' },
    clerkPerms.can_ring_up_spares_sales && { path: '/auto-spares/clerk-ringup', icon: ShoppingCart, label: 'Auto Spares — Ring Up' },
  ].filter(Boolean);

  const clerkMenu = [
    clerkPerms.can_view_dashboard && { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    clerkPerms.can_view_group_overview && {
      type: 'section', label: 'Dashboard', icon: LayoutDashboard, key: 'sec-dashboard',
      items: [
        { path: '/admin/group-overview', icon: Building2, label: 'JSM Group Overview' },
      ],
    },
    // Priority shortcuts — only shown if the clerk holds at least one of the
    // gating permissions. Each individual link is still permission-gated.
    (clerkPerms.can_manage_fuel_tracker || clerkPerms.can_view_group_overview) && (() => {
      const quickItems = [
        clerkPerms.can_manage_fuel_tracker && { path: '/admin/fuel-tracker', icon: Fuel, label: 'Fuel Tracker' },
        clerkPerms.can_manage_fuel_tracker && { path: '/admin/fleet-maintenance', icon: Wrench, label: 'Fleet Maintenance' },
        { path: '/admin/leave', icon: CalendarDays, label: 'Staff Leave Manager' },
        { path: '/admin/fleet-trips', icon: Package, label: 'Fleet Trips (Collective)' },
        { path: '/admin/job-cards', icon: ClipboardList, label: 'Job Cards' },
      ].filter(Boolean);
      return quickItems.length > 0
        ? { type: 'section', label: 'Management Quick View', icon: Trophy, key: 'sec-quickview', items: quickItems }
        : null;
    })(),
    (clerkPerms.can_manage_drivers || clerkPerms.can_manage_clerks || clerkPerms.can_view_tenders) && {
      type: 'section', label: 'Operations', icon: Truck, key: 'sec-operations',
      items: [
        clerkPerms.can_manage_drivers && { path: '/admin/drivers', icon: Users, label: 'Owner Drivers' },
        clerkPerms.can_manage_clerks && { path: '/admin/clerks', icon: UserCog, label: 'Transport Clerks' },
        { path: '/admin/job-cards', icon: ClipboardList, label: 'Job Cards' },
        clerkPerms.can_view_tenders && { path: '/admin/tenders', icon: Briefcase, label: 'Live Tenders' },
      ].filter(Boolean),
    },
    clerkPerms.can_manage_employees && {
      type: 'section', label: 'HR & Staff', icon: UserCog, key: 'sec-hr',
      items: [
        { path: '/admin/employees', icon: UserCog, label: 'Employees' },
        { path: '/admin/company-register', icon: CalendarDays, label: 'Company Register' },
      ],
    },
    clerkPerms.can_manage_fuel_tracker && {
      type: 'section', label: 'Fleet', icon: Fuel, key: 'sec-fleet',
      items: [
        { path: '/admin/fuel-tracker', icon: Fuel, label: 'Fuel Tracker' },
        { path: '/admin/fleet-maintenance', icon: Wrench, label: 'Fleet Maintenance' },
      ],
    },
    clerkAutoSpares.length > 0 && {
      type: 'section', label: 'Auto Spares', icon: ShoppingCart, key: 'sec-autospares',
      items: clerkAutoSpares,
    },
    (clerkPerms.can_manage_loans_tracker || clerkPerms.can_vet_loans) && {
      type: 'section', label: '2K2DAY', icon: Brain, key: 'sec-2k2day',
      items: [
        clerkPerms.can_manage_loans_tracker && { path: '/loans-tracker', icon: FileText, label: '2K2Day Loan Tracker' },
        clerkPerms.can_vet_loans && { path: '/vetting', icon: ScanSearch, label: '2K2Day Vetting Engine' },
      ].filter(Boolean),
    },
    (statementsItems.length > 0 || financeItems.length > 0 || bankingItems.length > 0 || clerkPerms.can_manage_exports) && {
      type: 'section', label: 'Finance', icon: DollarSign, key: 'sec-finance',
      items: [
        financeItems.length > 0 && { type: 'group', label: 'Finance', icon: DollarSign, key: 'finance', items: financeItems },
        bankingItems.length > 0 && { type: 'group', label: 'Banking', icon: Landmark, key: 'banking', items: bankingItems },
        statementsItems.length > 0 && { type: 'group', label: 'Statements', icon: FileText, key: 'statements', items: statementsItems },
        clerkPerms.can_manage_exports && { path: '/admin/exports', icon: Download, label: 'Exports' },
      ].filter(Boolean),
    },
    clerkPerms.can_manage_registrations && {
      type: 'section', label: 'Recruitment & Registrations', icon: ClipboardList, key: 'sec-recruitment',
      items: [
        { path: '/admin/registrations', icon: ClipboardList, label: 'Registrations' },
        { path: '/admin/applications', icon: ClipboardList, label: 'New Applications' },
      ],
    },
    (clerkPerms.can_view_credentials || clerkPerms.can_view_admin_permissions || clerkPerms.can_view_audit_logs || clerkPerms.can_manage_backups) && {
      type: 'section', label: 'Administration', icon: Settings, key: 'sec-administration',
      items: [
        clerkPerms.can_view_credentials && { path: '/admin/credentials', icon: KeyRound, label: 'Credentials' },
        clerkPerms.can_view_admin_permissions && { path: '/admin/permissions', icon: Shield, label: 'Admin Permissions' },
        clerkPerms.can_view_audit_logs && { path: '/admin/audit-logs', icon: History, label: 'Audit Logs' },
        clerkPerms.can_manage_backups && { path: '/admin/backups', icon: Archive, label: 'Backups & Recovery' },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  const isLinkActive = (path) => {
    if (path === '/admin' || path === '/driver') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    // Top-level collapsible section — can contain plain links AND / OR
    // nested `type: 'group'` sub-groups (used by Finance to keep the
    // existing Finance / Banking / Statements sub-menus intact).
    if (item.type === 'section') {
      const hasActiveLink = (list) => list.some((sub) => {
        if (sub.type === 'group') return sub.items.some((s) => isLinkActive(s.path));
        return isLinkActive(sub.path);
      });
      const isSectionActive = hasActiveLink(item.items);
      const isOpen = openMenus[item.key] || isSectionActive;
      return (
        <Collapsible key={item.key} open={isOpen} onOpenChange={() => toggleMenu(item.key)}>
          <CollapsibleTrigger className="w-full">
            <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full text-xs uppercase tracking-wider font-semibold
              ${isSectionActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
              <item.icon className="h-4 w-4" />
              <span className="flex-1 text-left">{item.label}</span>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="ml-2 mt-1 space-y-1 border-l-2 border-muted pl-2">
              {item.items.map((sub) => renderNavItem(sub))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    if (item.type === 'group') {
      const isGroupActive = item.items.some(subItem => isLinkActive(subItem.path));
      const isOpen = openMenus[item.key] || isGroupActive;
      
      return (
        <Collapsible key={item.key} open={isOpen} onOpenChange={() => toggleMenu(item.key)}>
          <CollapsibleTrigger className="w-full">
            <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors w-full
              ${isGroupActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
              <item.icon className="h-5 w-5" />
              <span className="font-medium flex-1 text-left">{item.label}</span>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-muted pl-2">
              {item.items.map((subItem) => (
                <Link
                  key={subItem.path}
                  to={subItem.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm
                    ${isLinkActive(subItem.path) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                >
                  <subItem.icon className="h-4 w-4" />
                  <span>{subItem.label}</span>
                </Link>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors
          ${isLinkActive(item.path) 
            ? 'bg-primary text-primary-foreground' 
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  const hrAdminMenu = [
    { path: '/hr', icon: Scale, label: 'Virtual Mediator' },
  ];
  const rawNavItems = user?.role === 'clerk'
    ? clerkMenu
    : user?.role === 'hr_admin'
      ? hrAdminMenu
      : (isAdmin ? adminMenu : driverLinks);
  // Remove any items whose path is in the admin-hidden list.
  // Recurses into `type: 'section'` (top-level groups) and `type: 'group'`
  // (sub-menus like Finance/Banking/Statements) — an empty section is dropped.
  const filterHidden = (items) => items
    .map((it) => {
      if (!it) return null;
      if (it.type === 'section') {
        const subs = filterHidden(it.items || []);
        if (!subs.length) return null;
        return { ...it, items: subs };
      }
      if (it.type === 'group') {
        const subs = (it.items || []).filter(s => !hiddenPaths.has(s.path));
        if (!subs.length) return null;
        return { ...it, items: subs };
      }
      if (it.path && hiddenPaths.has(it.path)) return null;
      return it;
    })
    .filter(Boolean);
  const navItems = filterHidden(rawNavItems);

  return (
    <div className="min-h-screen bg-background">
      <InactivityLock user={user} />
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <div className="flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg">JSM Portal</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:inline">{user?.full_name}</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleHome}
            title="Back to main menu"
            data-testid="header-home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleLogout} title="Sign out" data-testid="header-logout-btn">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r z-50 transform transition-transform duration-200
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar header */}
        <div className="h-16 flex items-center gap-3 px-4 border-b">
          <Truck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="font-bold text-lg">JSM Portal</h1>
            <p className="text-xs text-muted-foreground capitalize">{user?.role} Panel</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {navItems.map(renderNavItem)}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-card">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-6">
          {user?.role === 'clerk' && <ClerkStatusBanner />}
          <EodCheckBanner />
          {children}
        </div>
      </main>

      {/* Clerk-only daily reminder pop-up — fires once per fresh login */}
      <ClerkLoginReminder />
    </div>
  );
};

export default DashboardLayout;
